import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Edit, History, Loader, Pause, Pill, Play, Search, X, XCircle } from 'lucide-react';
import {
  ListView,
  ListActionBar,
  StatusPill,
  ExpirationPill,
  ConfirmDialog,
  ReasonDialog,
  ClinicalSafetyNotice,
} from '../common';
import PrimaryButton from '../common/PrimaryButton';
import PrimaryButtonLight from '../common/PrimaryButtonLight';
import Dialog from '../common/Dialog';
import { useHasAnyPermission } from '../../hooks/usePermission';
import {
  fetchMedicationGroups,
  fetchMedicationHistory,
  addMedication,
  updateMedication,
  validateMedicationForm,
  Medication,
  MedicationFormData,
} from '../../services/patientmr/medications.service';
import { fetchDemographics } from '../../services/patientmr/demographics.service';
import type { MedicationGroups, MedicationHistoryEntry, MedicationStatus } from '../../dtos/patientmr/medication.dto';
import { MED_ROUTE_OPTIONS, formatMedRoute, toMedRouteCode } from '../../constants/medRoutes';
import { summarizeAllergies } from './lifecycle/usePatientChartSummary';
import type { AllergySummary } from './lifecycle/usePatientChartSummary';
import type { PermissionString } from '../../types/permissions';

interface MedicationsProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId: string;
  /** Allergy summary already loaded for the chart header; fetched here when absent. */
  allergies?: AllergySummary;
}

// Dose unit options
const DOSE_UNIT_OPTIONS = [
  'mg (milligrams)',
  'mcg (micrograms)',
  'mL (milliliters)',
  'g (grams)',
  'mg/mL',
  'mcg/mL',
  '%',
  'units',
  'IU',
  'tablet(s)',
  'capsule(s)',
  'drop(s)',
  'patch(es)',
  'Other'
];

// Frequency options
const FREQUENCY_OPTIONS = [
  'Once daily',
  'Twice daily (BID)',
  'Three times daily (TID)',
  'Four times daily (QID)',
  'Every 4 hours (Q4H)',
  'Every 6 hours (Q6H)',
  'Every 8 hours (Q8H)',
  'Every 12 hours (Q12H)',
  'Every 24 hours',
  'Every 48 hours',
  'Every 72 hours',
  'Weekly',
  'Monthly',
  'PRN (as needed)',
  'Other'
];

const READ_PERMS = ['patientmr:phi:medications:read', 'patientmr:phi:medications:readall'] as PermissionString[];
const CREATE_PERMS = ['patientmr:phi:medications:create', 'patientmr:phi:medications:createall'] as PermissionString[];
const UPDATE_PERMS = ['patientmr:phi:medications:update', 'patientmr:phi:medications:updateall'] as PermissionString[];

/** Field labels used in the history drawer */
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  dose: 'Dose',
  route: 'Route',
  routeOther: 'Route (other)',
  frequency: 'Frequency',
  instructions: 'Instructions',
  expirationDate: 'Expiration date',
  status: 'Status',
  prn: 'PRN',
  prnIndication: 'PRN indication',
  startDate: 'Start date',
  orderingPhysician: 'Ordering physician',
  discontinuationReason: 'Discontinue reason',
  onHoldReason: 'Hold reason',
};

/** Returns today's date + 1 month as YYYY-MM-DD, the default expiration on new medications. */
const getDefaultExpirationDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

const emptyForm = (): MedicationFormData => ({
  name: '',
  dose: '',
  route: '',
  routeOther: '',
  frequency: '',
  instructions: '',
  expirationDate: getDefaultExpirationDate(),
  status: 'ACTIVE',
  prn: false,
  prnIndication: '',
  startDate: '',
  orderingPhysician: { name: '', npi: '' },
});

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const datePart = value.slice(0, 10);
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return value;
  return `${m}/${d}/${y}`;
};

/** Plain-text rendering of a history value (route codes → labels, booleans → Yes/No). */
export function formatHistoryValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (field === 'route' && typeof value === 'string') return formatMedRoute(value);
  if (typeof value === 'object') {
    const v = value as { name?: string; npi?: string };
    if (v.name !== undefined || v.npi !== undefined) {
      return [v.name, v.npi ? `NPI ${v.npi}` : ''].filter(Boolean).join(', ') || '—';
    }
    return JSON.stringify(value);
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value)) && field.toLowerCase().includes('date')) return formatDate(String(value));
  return String(value);
}

// ─── Allergy line ────────────────────────────────────────────────────────────

const AllergyLine: React.FC<{ summary: AllergySummary }> = ({ summary }) => {
  const base = 'rounded-md border px-3 py-2 text-sm text-left';
  switch (summary.state) {
    case 'list':
      return (
        <div className={`${base} border-red-200 bg-red-50 font-medium text-red-800`} data-testid="medications-allergies">
          Allergies: {summary.allergens.join(', ')}
        </div>
      );
    case 'none-known':
      return <div className={`${base} border-slate-200 bg-white text-slate-700`} data-testid="medications-allergies">No known allergies</div>;
    case 'not-recorded':
      return (
        <div className={`${base} border-amber-200 bg-amber-50 font-medium text-amber-800`} data-testid="medications-allergies">
          Allergies not recorded. Record them in Demographics before giving medications.
        </div>
      );
    case 'loading':
      return <div className={`${base} border-slate-200 bg-white text-slate-400`} data-testid="medications-allergies">Allergies: loading…</div>;
    default:
      return <div className={`${base} border-slate-200 bg-white text-slate-400`} data-testid="medications-allergies">Allergies: —</div>;
  }
};

// ─── Medication card ─────────────────────────────────────────────────────────

interface MedicationCardProps {
  medication: Medication;
  canUpdate: boolean;
  onEdit: (m: Medication) => void;
  onToggleHold: (m: Medication) => void;
  onDiscontinue: (m: Medication) => void;
  onHistory: (m: Medication) => void;
}

const ExpirationBadge: React.FC<{ medication: Medication }> = ({ medication }) => {
  const level = medication.expirationAlertLevel;
  const days = medication.expirationDaysRemaining;
  if (!medication.expirationDate && !level) return null;
  if (medication.expirationIsExpired || level === 'expired') return <ExpirationPill status="expired" text="Expired" />;
  if (level === 'red' || level === 'orange' || level === 'yellow') {
    return <ExpirationPill status={level} text={`Expires in ${days}d`} />;
  }
  if (level === 'green') return <ExpirationPill status="green" text={formatDate(medication.expirationDate)} />;
  return null;
};

const actionBtn = 'inline-flex min-h-[44px] sm:min-h-[36px] items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors';

const MedicationCard: React.FC<MedicationCardProps> = ({ medication, canUpdate, onEdit, onToggleHold, onDiscontinue, onHistory }) => {
  const inactive = medication.status === 'DISCONTINUED';
  const route = formatMedRoute(medication.route, medication.routeOther);
  return (
    <li className="rounded-md border border-slate-200 bg-white p-3" data-testid={`medication-${medication.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-medium text-slate-900 break-words ${inactive ? 'text-slate-500' : ''}`}>{medication.name}</span>
            {medication.prn && (
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-800">PRN</span>
            )}
            <StatusPill status={medication.status} />
            <ExpirationBadge medication={medication} />
          </div>
          <div className="mt-1 text-sm text-slate-600 break-words">
            {[medication.dose, route, medication.frequency].filter(Boolean).join(' • ')}
          </div>
          {medication.prn && medication.prnIndication && (
            <div className="mt-0.5 text-xs text-slate-600">PRN for: {medication.prnIndication}</div>
          )}
          {medication.instructions && <div className="mt-0.5 text-xs text-slate-500 break-words">{medication.instructions}</div>}
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
            {medication.startDate && <span>Started {formatDate(medication.startDate)}</span>}
            {medication.orderingPhysician?.name && (
              <span>
                Ordered by {medication.orderingPhysician.name}
                {medication.orderingPhysician.npi ? ` (NPI ${medication.orderingPhysician.npi})` : ''}
              </span>
            )}
          </div>
          {medication.status === 'ON_HOLD' && medication.onHoldReason && (
            <div className="mt-1 text-xs text-amber-800">On hold: {medication.onHoldReason}</div>
          )}
          {inactive && (
            <div className="mt-1 text-xs text-slate-600" data-testid="discontinue-reason">
              Discontinued{medication.discontinuedAt ? ` ${formatDate(medication.discontinuedAt)}` : ''}
              {medication.discontinuationReason ? `: ${medication.discontinuationReason}` : ''}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {canUpdate && !inactive && (
          <button type="button" onClick={() => onEdit(medication)} className={`${actionBtn} text-blue-700 hover:bg-blue-50`}>
            <Edit size={14} aria-hidden="true" /> Edit
          </button>
        )}
        {canUpdate && medication.status === 'ACTIVE' && (
          <button type="button" onClick={() => onToggleHold(medication)} className={`${actionBtn} text-amber-700 hover:bg-amber-50`}>
            <Pause size={14} aria-hidden="true" /> Hold
          </button>
        )}
        {canUpdate && medication.status === 'ON_HOLD' && (
          <button type="button" onClick={() => onToggleHold(medication)} className={`${actionBtn} text-green-700 hover:bg-green-50`}>
            <Play size={14} aria-hidden="true" /> Resume
          </button>
        )}
        {canUpdate && !inactive && (
          <button type="button" onClick={() => onDiscontinue(medication)} className={`${actionBtn} text-red-700 hover:bg-red-50`}>
            <XCircle size={14} aria-hidden="true" /> Discontinue
          </button>
        )}
        <button type="button" onClick={() => onHistory(medication)} className={`${actionBtn} text-slate-700 hover:bg-slate-100`}>
          <History size={14} aria-hidden="true" /> History
        </button>
      </div>
    </li>
  );
};

// ─── Section ─────────────────────────────────────────────────────────────────

const MedicationSection: React.FC<{
  id: string;
  title: string;
  items: Medication[];
  open: boolean;
  onToggle: () => void;
  emptyText: string;
  renderItem: (m: Medication) => React.ReactNode;
}> = ({ id, title, items, open, onToggle, emptyText, renderItem }) => (
  <section className="rounded-md border border-slate-200 bg-slate-50" data-testid={`med-section-${id}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`med-section-${id}-body`}
      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-slate-700"
    >
      <span>{title} ({items.length})</span>
      {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
    </button>
    {open && (
      <div id={`med-section-${id}-body`} className="px-2 pb-2">
        {items.length === 0 ? (
          <p className="px-1 py-2 text-sm text-slate-500">{emptyText}</p>
        ) : (
          <ul className="space-y-2">{items.map(renderItem)}</ul>
        )}
      </div>
    )}
  </section>
);

// ─── History drawer ──────────────────────────────────────────────────────────

const HistoryDrawer: React.FC<{
  medication: Medication | null;
  entries: MedicationHistoryEntry[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
}> = ({ medication, entries, loading, error, onClose }) => {
  useEffect(() => {
    if (!medication) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [medication, onClose]);

  if (!medication) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="med-history-title"
        className="h-full w-full max-w-md overflow-y-auto bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h3 id="med-history-title" className="text-lg font-semibold text-slate-800 text-left">
            History: {medication.name}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close history" className="rounded p-1 text-slate-500 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-600">
            <Loader size={16} className="animate-spin" /> Loading history…
          </div>
        )}
        {!loading && error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {!loading && !error && entries.length === 0 && <p className="text-sm text-slate-500">No changes recorded.</p>}
        {!loading && !error && entries.length > 0 && (
          <ol className="space-y-3" data-testid="medication-history">
            {entries.map((entry, idx) => (
              <li key={`${entry.changedAt}-${idx}`} className="rounded border border-slate-200 p-2 text-left">
                <div className="text-xs text-slate-500">
                  {new Date(entry.changedAt).toLocaleString()} · {entry.changedByName || entry.changedBy}
                  {entry.action ? ` · ${entry.action}` : ''}
                </div>
                {entry.changes && Object.keys(entry.changes).length > 0 ? (
                  <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
                    {Object.entries(entry.changes).map(([field, change]) => (
                      <li key={field} className="break-words">
                        <span className="font-medium">{FIELD_LABELS[field] ?? field}:</span>{' '}
                        {formatHistoryValue(field, change.from)} → {formatHistoryValue(field, change.to)}
                      </li>
                    ))}
                  </ul>
                ) : entry.prior ? (
                  <ul className="mt-1 space-y-0.5 text-sm text-slate-700">
                    <li className="text-xs text-slate-500">Values before this change:</li>
                    {Object.entries(entry.prior)
                      .filter(([field]) => field in FIELD_LABELS)
                      .map(([field, value]) => (
                        <li key={field} className="break-words">
                          <span className="font-medium">{FIELD_LABELS[field]}:</span> {formatHistoryValue(field, value)}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-slate-600">Created</p>
                )}
              </li>
            ))}
          </ol>
        )}
      </aside>
    </div>
  );
};

// ─── Form dialog ─────────────────────────────────────────────────────────────

const MedicationFormDialog: React.FC<{
  open: boolean;
  mode: 'add' | 'edit';
  initial: MedicationFormData;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (data: MedicationFormData) => void;
}> = ({ open, mode, initial, submitting, onCancel, onSubmit }) => {
  const [form, setForm] = useState<MedicationFormData>(initial);
  const [doseAmount, setDoseAmount] = useState('');
  const [doseUnit, setDoseUnit] = useState('');
  const [doseUnitOther, setDoseUnitOther] = useState(false);
  const [frequencyOther, setFrequencyOther] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setForm(initial);
    const parts = initial.dose.trim().split(/\s+/);
    const amount = parts[0] ?? '';
    const unit = parts.slice(1).join(' ');
    setDoseAmount(amount);
    setDoseUnit(unit);
    setDoseUnitOther(!!unit && !DOSE_UNIT_OPTIONS.includes(unit));
    setFrequencyOther(!!initial.frequency && !FREQUENCY_OPTIONS.includes(initial.frequency));
    setErrors({});
  }, [open, initial]);

  const setDose = (amount: string, unit: string) => {
    setDoseAmount(amount);
    setDoseUnit(unit);
    setForm((f) => ({ ...f, dose: `${amount} ${unit}`.trim() }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateMedicationForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const physician = form.orderingPhysician;
    onSubmit({
      ...form,
      routeOther: form.route === 'OTHER' ? form.routeOther?.trim() : '',
      prnIndication: form.prn ? form.prnIndication?.trim() : '',
      orderingPhysician: physician && (physician.name.trim() || physician.npi?.trim())
        ? { name: physician.name.trim(), npi: physician.npi?.trim() || undefined }
        : undefined,
    });
  };

  const input = 'w-full rounded-md border border-gray-300 p-2 text-base focus:border-slate-500 focus:ring-2 focus:ring-slate-500';
  const label = 'mb-1 block text-sm font-medium text-gray-700 text-left';
  const err = (key: string) => (errors[key] ? <p className="mt-1 text-xs text-red-700" role="alert">{errors[key]}</p> : null);

  return (
    <Dialog open={open} onClose={() => { if (!submitting) onCancel(); }} labelledBy="med-form-title">
      <form onSubmit={handleSubmit} className="space-y-4 text-left" noValidate>
        <h3 id="med-form-title" className="text-lg font-medium text-gray-900">
          {mode === 'add' ? 'Add New Medication' : 'Edit Medication'}
        </h3>

        <div>
          <label htmlFor="med-name" className={label}>Medication Name*</label>
          <input id="med-name" type="text" className={input} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Levetiracetam" />
          {err('name')}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="med-dose-amount" className={label}>Dose Amount*</label>
            <input id="med-dose-amount" type="text" inputMode="decimal" className={input} value={doseAmount}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d*$/.test(value)) setDose(value, doseUnit);
              }}
              placeholder="e.g., 10" />
          </div>
          <div>
            <label htmlFor="med-dose-unit" className={label}>Dose Unit*</label>
            <select id="med-dose-unit" className={input}
              value={doseUnitOther ? 'Other' : doseUnit}
              onChange={(e) => {
                if (e.target.value === 'Other') {
                  setDoseUnitOther(true);
                  setDose(doseAmount, '');
                } else {
                  setDoseUnitOther(false);
                  setDose(doseAmount, e.target.value);
                }
              }}>
              <option value="">Select unit...</option>
              {DOSE_UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
            </select>
            {doseUnitOther && (
              <input type="text" aria-label="Other dose unit" className={`${input} mt-2`} value={doseUnit}
                onChange={(e) => setDose(doseAmount, e.target.value)} placeholder="Specify other unit" />
            )}
          </div>
        </div>
        {err('dose')}

        <div>
          <label htmlFor="med-route" className={label}>Route*</label>
          <select id="med-route" className={input} value={form.route}
            onChange={(e) => setForm({ ...form, route: e.target.value })}>
            <option value="">Select route...</option>
            {MED_ROUTE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {err('route')}
          {form.route === 'OTHER' && (
            <>
              <input type="text" aria-label="Other route" className={`${input} mt-2`} value={form.routeOther ?? ''}
                onChange={(e) => setForm({ ...form, routeOther: e.target.value })} placeholder="Specify other route" />
              {err('routeOther')}
            </>
          )}
        </div>

        <div>
          <label htmlFor="med-frequency" className={label}>Frequency*</label>
          <select id="med-frequency" className={input}
            value={frequencyOther ? 'Other' : form.frequency}
            onChange={(e) => {
              if (e.target.value === 'Other') {
                setFrequencyOther(true);
                setForm({ ...form, frequency: '' });
              } else {
                setFrequencyOther(false);
                setForm({ ...form, frequency: e.target.value, prn: e.target.value === 'PRN (as needed)' ? true : form.prn });
              }
            }}>
            <option value="">Select frequency...</option>
            {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          {frequencyOther && (
            <input type="text" aria-label="Other frequency" className={`${input} mt-2`} value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })} placeholder="Specify other frequency" />
          )}
          {err('frequency')}
        </div>

        <div className="rounded-md border border-slate-200 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" checked={!!form.prn}
              onChange={(e) => setForm({ ...form, prn: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300" />
            PRN (give as needed)
          </label>
          {form.prn && (
            <div className="mt-2">
              <label htmlFor="med-prn-indication" className={label}>PRN Indication*</label>
              <input id="med-prn-indication" type="text" className={input} value={form.prnIndication ?? ''}
                onChange={(e) => setForm({ ...form, prnIndication: e.target.value })}
                placeholder="e.g., Seizure lasting over 5 minutes" />
              {err('prnIndication')}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="med-instructions" className={label}>Instructions*</label>
          <textarea id="med-instructions" className={input} rows={3} value={form.instructions}
            onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            placeholder="e.g., Give via G-tube, flush with 10 mL water" />
          {err('instructions')}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="med-physician-name" className={label}>Ordering Physician</label>
            <input id="med-physician-name" type="text" className={input} value={form.orderingPhysician?.name ?? ''}
              onChange={(e) => setForm({ ...form, orderingPhysician: { name: e.target.value, npi: form.orderingPhysician?.npi ?? '' } })}
              placeholder="Physician name" />
          </div>
          <div>
            <label htmlFor="med-physician-npi" className={label}>Physician NPI</label>
            <input id="med-physician-npi" type="text" inputMode="numeric" maxLength={10} className={input}
              value={form.orderingPhysician?.npi ?? ''}
              onChange={(e) => setForm({ ...form, orderingPhysician: { name: form.orderingPhysician?.name ?? '', npi: e.target.value.replace(/\D/g, '') } })}
              placeholder="10 digits" />
            {err('orderingPhysicianNpi')}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="med-start" className={label}>Start Date</label>
            <input id="med-start" type="date" className={input} value={form.startDate ?? ''}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </div>
          <div>
            <label htmlFor="med-expiration" className={label}>Expiration Date</label>
            <input id="med-expiration" type="date" className={input} value={form.expirationDate}
              onChange={(e) => setForm({ ...form, expirationDate: e.target.value })} />
          </div>
          <div>
            <label htmlFor="med-status" className={label}>Status*</label>
            <select id="med-status" className={input} value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as MedicationStatus })}>
              <option value="ACTIVE">Active</option>
              <option value="ON_HOLD">On Hold</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
          <PrimaryButtonLight type="button" onClick={onCancel} disabled={submitting} className="w-full justify-center sm:w-auto min-h-[44px]">
            Cancel
          </PrimaryButtonLight>
          <PrimaryButton type="submit" disabled={submitting} className="w-full justify-center sm:w-auto min-h-[44px] disabled:opacity-50">
            {submitting ? (
              <span className="flex items-center justify-center"><Loader size={16} className="mr-2 animate-spin" />Saving...</span>
            ) : mode === 'add' ? 'Add Medication' : 'Update Medication'}
          </PrimaryButton>
        </div>
      </form>
    </Dialog>
  );
};

/** Form values for editing an existing medication (legacy routes map to codes). */
function toFormData(m: Medication): MedicationFormData {
  const code = toMedRouteCode(m.route);
  const legacyText = code === 'OTHER' && m.route !== 'OTHER' ? m.route : '';
  return {
    name: m.name,
    dose: m.dose || '',
    route: code,
    routeOther: m.routeOther || legacyText,
    frequency: m.frequency || '',
    instructions: m.instructions || '',
    expirationDate: m.expirationDate || '',
    status: m.status === 'DISCONTINUED' ? 'ACTIVE' : m.status,
    prn: !!m.prn,
    prnIndication: m.prnIndication || '',
    startDate: m.startDate || '',
    orderingPhysician: { name: m.orderingPhysician?.name || '', npi: m.orderingPhysician?.npi || '' },
  };
}

// ─── Main ────────────────────────────────────────────────────────────────────

const EMPTY_GROUPS: MedicationGroups = { active: [], onHold: [], inactive: [] };

const Medications: React.FC<MedicationsProps> = ({ expanded, toggleExpanded, patientId, allergies }) => {
  const canRead = useHasAnyPermission(READ_PERMS);
  const canCreate = useHasAnyPermission(CREATE_PERMS);
  const canUpdate = useHasAnyPermission(UPDATE_PERMS);

  const [groups, setGroups] = useState<MedicationGroups>(EMPTY_GROUPS);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState({ active: true, onHold: true, inactive: false });

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formInitial, setFormInitial] = useState<MedicationFormData>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [holdTarget, setHoldTarget] = useState<Medication | null>(null);
  const [discontinueTarget, setDiscontinueTarget] = useState<Medication | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [historyFor, setHistoryFor] = useState<Medication | null>(null);
  const [historyEntries, setHistoryEntries] = useState<MedicationHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [ownAllergies, setOwnAllergies] = useState<AllergySummary>({ state: 'loading' });
  const allergySummary = allergies ?? ownAllergies;

  const loadedFor = useRef<string | null>(null);

  const load = useCallback(async (mode: 'load' | 'refresh' = 'load') => {
    if (!patientId) return;
    if (mode === 'refresh') setRefreshing(true); else setLoading(true);
    setError('');
    try {
      setGroups(await fetchMedicationGroups(patientId));
    } catch {
      setError('Failed to load medications. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (!expanded || !patientId) {
      if (!expanded) loadedFor.current = null;
      return;
    }
    if (loadedFor.current === patientId) return;
    loadedFor.current = patientId;
    void load();
  }, [expanded, patientId, load]);

  // Allergy line: use the header's summary when given, else read demographics once
  useEffect(() => {
    if (allergies || !patientId || !expanded) return;
    let cancelled = false;
    fetchDemographics(patientId)
      .then((d) => { if (!cancelled) setOwnAllergies(summarizeAllergies(d)); })
      .catch(() => { if (!cancelled) setOwnAllergies({ state: 'unavailable' }); });
    return () => { cancelled = true; };
  }, [allergies, patientId, expanded]);

  const flash = (message: string) => {
    setSuccessMessage(message);
    window.setTimeout(() => setSuccessMessage(''), 5000);
  };

  const filtered = useMemo<MedicationGroups>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    const match = (m: Medication) =>
      [m.name, m.dose, formatMedRoute(m.route, m.routeOther), m.frequency, m.prnIndication]
        .some((v) => (v || '').toLowerCase().includes(q));
    return { active: groups.active.filter(match), onHold: groups.onHold.filter(match), inactive: groups.inactive.filter(match) };
  }, [groups, search]);

  const openAdd = () => {
    setFormMode('add');
    setFormInitial(emptyForm());
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (m: Medication) => {
    setFormMode('edit');
    setFormInitial(toFormData(m));
    setEditingId(m.id);
    setFormOpen(true);
  };

  const handleSubmit = async (data: MedicationFormData) => {
    setSubmitting(true);
    setError('');
    try {
      if (formMode === 'add') {
        await addMedication(patientId, data);
        flash('Medication added successfully');
      } else if (editingId) {
        await updateMedication(patientId, editingId, data);
        flash('Medication updated successfully');
      }
      setFormOpen(false);
      await load('refresh');
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save medication. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmToggleHold = async () => {
    if (!holdTarget) return;
    const next: MedicationStatus = holdTarget.status === 'ACTIVE' ? 'ON_HOLD' : 'ACTIVE';
    setActionBusy(true);
    try {
      await updateMedication(patientId, holdTarget.id, { status: next });
      flash(next === 'ON_HOLD' ? 'Medication put on hold' : 'Medication resumed');
      setHoldTarget(null);
      await load('refresh');
    } catch {
      setError(next === 'ON_HOLD' ? 'Failed to put the medication on hold.' : 'Failed to resume the medication.');
      setHoldTarget(null);
    } finally {
      setActionBusy(false);
    }
  };

  const confirmDiscontinue = async (reason: string) => {
    if (!discontinueTarget) return;
    setActionBusy(true);
    try {
      await updateMedication(patientId, discontinueTarget.id, { status: 'DISCONTINUED', discontinuationReason: reason });
      flash('Medication discontinued');
      setDiscontinueTarget(null);
      await load('refresh');
    } catch {
      setError('Failed to discontinue medication. Please try again.');
      setDiscontinueTarget(null);
    } finally {
      setActionBusy(false);
    }
  };

  const openHistory = async (m: Medication) => {
    setHistoryFor(m);
    setHistoryEntries([]);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      setHistoryEntries(await fetchMedicationHistory(patientId, m.id));
    } catch {
      setHistoryError('Could not load the history. Try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = useCallback(() => setHistoryFor(null), []);

  const renderCard = (m: Medication) => (
    <MedicationCard
      key={m.id}
      medication={m}
      canUpdate={canUpdate}
      onEdit={openEdit}
      onToggleHold={setHoldTarget}
      onDiscontinue={setDiscontinueTarget}
      onHistory={(med) => void openHistory(med)}
    />
  );

  if (!canRead) return null;

  return (
    <>
      <ListView title="Medications" icon={<Pill size={20} />} expanded={expanded} onToggleExpand={toggleExpanded}>
        <div className="space-y-3">
          <ClinicalSafetyNotice />
          <AllergyLine summary={allergySummary} />

          {successMessage && (
            <div role="status" className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <span>{successMessage}</span>
              <button type="button" onClick={() => setSuccessMessage('')} aria-label="Dismiss" className="text-green-700"><X size={16} /></button>
            </div>
          )}

          <ListActionBar
            onRefresh={() => void load('refresh')}
            refreshing={refreshing}
            loading={loading}
            newButtonLabel="Add Medication"
            onNewClick={openAdd}
            showNew={canCreate}
          >
            <div className="relative w-full sm:w-64">
              <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                aria-label="Search medications"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search medications"
                className="h-9 w-full rounded-md border border-slate-300 pl-7 pr-2 text-sm"
              />
            </div>
          </ListActionBar>

          {error && (
            <div role="alert" className="flex items-start justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" onClick={() => setError('')} aria-label="Dismiss error"><X size={16} /></button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-600">
              <Loader size={24} className="mr-2 animate-spin" /> Loading...
            </div>
          ) : (
            <div className="space-y-3">
              <MedicationSection
                id="active"
                title="Active"
                items={filtered.active}
                open={openSections.active}
                onToggle={() => setOpenSections((s) => ({ ...s, active: !s.active }))}
                emptyText="No active medications."
                renderItem={renderCard}
              />
              <MedicationSection
                id="on-hold"
                title="On hold"
                items={filtered.onHold}
                open={openSections.onHold}
                onToggle={() => setOpenSections((s) => ({ ...s, onHold: !s.onHold }))}
                emptyText="No medications on hold."
                renderItem={renderCard}
              />
              <MedicationSection
                id="inactive"
                title="Inactive"
                items={filtered.inactive}
                open={openSections.inactive}
                onToggle={() => setOpenSections((s) => ({ ...s, inactive: !s.inactive }))}
                emptyText="No discontinued medications."
                renderItem={renderCard}
              />
            </div>
          )}
        </div>
      </ListView>

      <MedicationFormDialog
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        submitting={submitting}
        onCancel={() => setFormOpen(false)}
        onSubmit={(data) => void handleSubmit(data)}
      />

      <ConfirmDialog
        open={!!holdTarget}
        title={holdTarget?.status === 'ACTIVE' ? 'Put medication on hold?' : 'Resume medication?'}
        message={holdTarget?.status === 'ACTIVE'
          ? `${holdTarget?.name ?? ''} moves to On hold. It is left off the MAR picker and the plan of care until resumed.`
          : `${holdTarget?.name ?? ''} returns to the active list.`}
        confirmLabel={holdTarget?.status === 'ACTIVE' ? 'Put on hold' : 'Resume'}
        busy={actionBusy}
        onConfirm={confirmToggleHold}
        onCancel={() => setHoldTarget(null)}
      />

      <ReasonDialog
        open={!!discontinueTarget}
        title="Discontinue medication"
        description={discontinueTarget ? `Discontinuing ${discontinueTarget.name}. It moves to Inactive and cannot be restarted; add it again if it is re-ordered.` : undefined}
        label="Discontinue reason"
        placeholder="Clinical reason for discontinuing"
        minLength={3}
        maxLength={500}
        confirmLabel="Discontinue"
        destructive
        busy={actionBusy}
        onConfirm={confirmDiscontinue}
        onCancel={() => setDiscontinueTarget(null)}
      />

      <HistoryDrawer
        medication={historyFor}
        entries={historyEntries}
        loading={historyLoading}
        error={historyError}
        onClose={closeHistory}
      />
    </>
  );
};

export default Medications;
